/**
 * Read-only story progress for the full-screen chapter view
 * (src/components/ChapterSceneFullscreen.tsx). Deliberately NOT a
 * scrubber (product ask): no seek, no ±15s — a child left alone with the
 * picture would otherwise play with it to flick through the illustrations.
 * Pure so the clamping and chapter-marker placement are unit-testable.
 */
import type { StoryChapter } from "./types";

/** Fraction of the story already heard, clamped to [0, 1] — `0` while the
 *  duration isn't known yet (metadata still loading). */
export function storyProgressFraction(currentTime: number, duration: number): number {
  if (!(duration > 0) || !Number.isFinite(duration)) return 0;
  if (!Number.isFinite(currentTime)) return 0;
  return Math.min(1, Math.max(0, currentTime / duration));
}

/** Where each chapter after the first begins, as fractions of the story in
 *  (0, 1) — drawn as small notches so the bar also reads as "which part of
 *  the story we're in". The first chapter (starting at 0) and anything at
 *  or past the end have no visible boundary, so they're left out. */
export function chapterMarkerFractions(chapters: StoryChapter[], duration: number): number[] {
  if (!(duration > 0) || !Number.isFinite(duration)) return [];
  return chapters
    .map((c) => c.start_s / duration)
    .filter((f) => Number.isFinite(f) && f > 0 && f < 1);
}
